import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Dropdown from 'antd/lib/dropdown';
import Empty from 'antd/lib/empty';
import Input from 'antd/lib/input';
import Pagination from 'antd/lib/pagination';
import Popover from 'antd/lib/popover';
import Spin from 'antd/lib/spin';
import Tabs from 'antd/lib/tabs';
import {
    AimOutlined,
    CheckSquareOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    FilterOutlined,
    FolderFilled,
    LoadingOutlined,
    PlusOutlined,
    RocketOutlined,
    SearchOutlined,
    ToolFilled,
    UndoOutlined,
} from '@ant-design/icons';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '@/components/styles/globalStyles';
import { TriageIssueCard } from './TriageIssueCard';
import { PAYWALL_ISSUES } from './paywallIssues';
import { CreateIssueModal } from './CreateIssueModal';
import { MergeIssuesModal } from './MergeIssuesModal';
import { TriageFilters } from './TriageFilters';
import { buildTriageSearch, parseTriageParamsFromSearch, TRIAGE_QUERY_PARAMS } from './triageUrlParams';
import { hasAnyParamInSearch } from '@/helpers/urlParamHelpers';
import { tryFetchPaginatedEntities } from '@/actions/entities';
import {
    clearPinnedIssue,
    setTriageFilters,
    setTriageReleaseFilter,
    setTriageSearch,
    setTriageSortBy,
    setTriageStatusGroup,
    TriageReleaseFilter,
    TriageSortBy,
    TriageStatusGroup,
    TriageViewType,
    TRY_BULK_REOPEN_ISSUES,
    TRY_BULK_TRANSITION_ISSUES,
    tryBulkReopenIssues,
    tryBulkTransitionIssues,
} from '@/actions/triage';
import { FetchableEntityType, FetchablePaginatedEntityType } from '@/entityType';
import { RootState } from '@/reducers/reducers';
import {
    selectAppHome,
    selectAppIssues,
    selectIssuesCounts,
    selectPaginatedIssuesLimitAndOffset,
    selectRelease,
    selectTriageFilters,
} from '@/selectors/selectors';
import { useGetEntity, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { AppComponentProps, FeatureSection } from '@/typescriptHelpers';
import { IssueEntity, IssueState, SubscriptionFeatureType } from '@/types';
import { EmptyStateWrapper } from '@/components/releaseEmptyState/EmptyStateWrapper';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import {
    useFeaturePlanRestrictionTooltipContent,
    useRestrictedFeatureIsDisabled,
} from '@/components/planRestrictions/planRestrictionHelperComponents';

const SORT_OPTIONS: { value: TriageSortBy; label: string }[] = [
    { value: 'triage', label: 'Triage' },
    { value: 'date', label: 'Date' },
    { value: 'destinations', label: 'Destination' },
    { value: 'owner', label: 'Owner' },
    { value: 'source', label: 'Signal' },
];

const sortLabel = (sortBy: TriageSortBy): string => SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Triage';

const PAGE_SIZE = 20;

const filterButtonTextStyle: React.CSSProperties = { color: antGrey.grey8 };
const filterButtonIconStyle: React.CSSProperties = { color: rwColors.primary };

const tabLabel = (label: string, count: number, isActive: boolean) => (
    <span style={{ fontWeight: 600, fontSize: 16, color: isActive ? rwColors.primary : antGrey.grey7 }}>
        {label}{' '}
        <span style={{ fontWeight: 400, color: isActive ? colorWithAlpha(rwColors.primary, 0.5) : antGrey.grey5 }}>
            - {count}
        </span>
    </span>
);

export const TriageIssuesList: React.FC<AppComponentProps & { releaseId?: string; basePath: string }> = ({
    orgId,
    appId,
    releaseId,
    basePath,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const shouldShowPaywall = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Triage);
    const paywallContent = useFeaturePlanRestrictionTooltipContent(SubscriptionFeatureType.Triage);

    const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = React.useState(false);

    // Multi-select mode: entered via the Select button. Keyed by issue id and holding the issue itself, so a
    // selection spanning several pages keeps working once the checked issues are no longer on screen.
    const [isSelectMode, setIsSelectMode] = React.useState(false);
    const [selectedById, setSelectedById] = React.useState<Record<string, IssueEntity>>({});
    const selectedIssueIds = React.useMemo(() => new Set(Object.keys(selectedById)), [selectedById]);
    const [isGroupModalOpen, setIsGroupModalOpen] = React.useState(false);
    const [isEscalateMode, setIsEscalateMode] = React.useState(false);
    const openGroupModal = (escalate: boolean) => {
        setIsEscalateMode(escalate);
        setIsGroupModalOpen(true);
    };
    const toggleIssueSelected = (issue: IssueEntity) =>
        setSelectedById((prev) => {
            if (prev[issue.id]) {
                const { [issue.id]: _removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [issue.id]: issue };
        });
    const clearSelection = () => setSelectedById({});
    const cancelSelect = () => {
        setIsSelectMode(false);
        clearSelection();
    };

    const viewType = releaseId ? TriageViewType.Release : TriageViewType.App;
    const isBulkTransitioning = useIsLoading(TRY_BULK_TRANSITION_ISSUES, appId);
    const bulkTransitionDone = useUpdated(TRY_BULK_TRANSITION_ISSUES, appId);
    // Clear the selection after a bulk action but stay in select mode so the user can keep acting.
    React.useEffect(() => {
        if (bulkTransitionDone) {
            clearSelection();
        }
    }, [bulkTransitionDone]);
    const onBulkNotAPriority = () =>
        dispatch(
            tryBulkTransitionIssues({
                orgId,
                appId,
                issueIds: Array.from(selectedIssueIds),
                targetState: IssueState.NotAPriority,
                viewType,
            }),
        );

    const isBulkReopening = useIsLoading(TRY_BULK_REOPEN_ISSUES, appId);
    const bulkReopenDone = useUpdated(TRY_BULK_REOPEN_ISSUES, appId);
    React.useEffect(() => {
        if (bulkReopenDone) {
            clearSelection();
        }
    }, [bulkReopenDone]);
    const onBulkReopen = () =>
        dispatch(tryBulkReopenIssues({ orgId, appId, issueIds: Array.from(selectedIssueIds), viewType }));

    const filters = useSelector((state: RootState) => selectTriageFilters(state, appId));

    // Local input value drives the field immediately; the committed search (which triggers the refetch) is debounced.
    const [searchInput, setSearchInput] = React.useState(filters.search);
    const searchTimer = React.useRef<ReturnType<typeof setTimeout>>();
    React.useEffect(() => () => clearTimeout(searchTimer.current), []);
    const onSearchChange = (next: string) => {
        setSearchInput(next);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => dispatch(setTriageSearch({ appId, search: next.trim() })), 300);
    };
    const commitSearch = (next: string) => {
        clearTimeout(searchTimer.current);
        dispatch(setTriageSearch({ appId, search: next.trim() }));
    };

    const [initialUrlParams] = React.useState(() =>
        hasAnyParamInSearch(location.search, Object.values(TRIAGE_QUERY_PARAMS)) ?
            parseTriageParamsFromSearch(location.search)
        :   null,
    );
    const [isUrlHydrated, setIsUrlHydrated] = React.useState(false);
    const [requestedOffset, setRequestedOffset] = React.useState(((initialUrlParams?.page ?? 1) - 1) * PAGE_SIZE);
    const hydratedFiltersKeyRef = React.useRef<string>();
    const filtersKey = buildTriageSearch(filters, 1, '');
    React.useEffect(() => {
        if (initialUrlParams) {
            dispatch(setTriageFilters({ appId, filters: initialUrlParams.filters }));
            setSearchInput(initialUrlParams.filters.search);
            hydratedFiltersKeyRef.current = buildTriageSearch(initialUrlParams.filters, 1, '');
        } else {
            hydratedFiltersKeyRef.current = filtersKey;
        }
        setIsUrlHydrated(true);
    }, []);

    const issues = useSelector((state: RootState) => selectAppIssues(state, appId));
    const { offset } = useSelector((state: RootState) => selectPaginatedIssuesLimitAndOffset(state, appId));

    const selectedIssues = React.useMemo(
        () => Object.values(selectedById).map((selected) => issues.find((i) => i.id === selected.id) ?? selected),
        [selectedById, issues],
    );
    // An issue with a fix request can't be grouped with another fix-request issue (one fix request only). Once one
    // is selected, the other fix-request issues' checkboxes are disabled, and the Group buttons swap for "Merge
    // into existing issue with fix request".
    const selectionHasFixRequest = selectedIssues.some((issue) => !!issue.fixRequest);

    // Live/Next release versions back the release filter buttons. Ensure appHome is loaded for them.
    const appHome = useSelector((state: RootState) => selectAppHome(state, appId));
    const liveVersion = appHome?.liveRelease?.version;
    const nextVersion = appHome?.nextRelease?.version;
    const [fetchAppHome] = useGetEntity(FetchableEntityType.AppHome, appId);
    React.useEffect(() => {
        fetchAppHome({ orgId, appId });
    }, [orgId, appId]);

    // At the release-level route, always scope to the release we're viewing. Otherwise the Live/Next
    // buttons take precedence, and falling back to the explicit Version filter from the popover.
    const releaseVersion = useSelector((state: RootState) =>
        releaseId ? selectRelease(state, releaseId)?.version : undefined,
    );
    let activeVersions: string[] = filters.versions;

    if (releaseId) {
        activeVersions = releaseVersion ? [releaseVersion] : [];
    } else if (filters.releaseFilter === 'live') {
        activeVersions = liveVersion ? [liveVersion] : [];
    } else if (filters.releaseFilter === 'next') {
        activeVersions = nextVersion ? [nextVersion] : [];
    }

    // The explicit Version filter only applies (and is shown) at app level with no Live/Next selected.
    const isVersionFilterActive = !releaseId && filters.releaseFilter === 'none';

    const appliedFilterCount =
        filters.statuses.length +
        filters.sources.length +
        filters.owners.length +
        filters.reporters.length +
        filters.insights.length +
        (isVersionFilterActive ? filters.versions.length : 0);

    const fetchIssues = React.useCallback(
        (statusGroup: TriageStatusGroup, pageOffset: number) => {
            setRequestedOffset(pageOffset);
            dispatch(
                tryFetchPaginatedEntities(
                    FetchablePaginatedEntityType.Issues,
                    {
                        orgId,
                        appId,
                        statusGroup,
                        statuses: filters.statuses,
                        sources: filters.sources,
                        owners: filters.owners,
                        reporters: filters.reporters,
                        versions: activeVersions,
                        destinations: filters.destinations,
                        insights: filters.insights,
                        sortBy: filters.sortBy,
                        search: filters.search || undefined,
                    },
                    PAGE_SIZE,
                    pageOffset,
                    appId,
                ),
            );
        },
        [
            dispatch,
            orgId,
            appId,
            filters.statuses,
            filters.sources,
            filters.owners,
            filters.reporters,
            filters.destinations,
            filters.insights,
            filters.sortBy,
            filters.search,
            activeVersions.join(','),
        ],
    );

    React.useEffect(() => {
        if (shouldShowPaywall || !isUrlHydrated) return;
        dispatch(clearPinnedIssue({ appId }));
        fetchIssues(filters.statusGroup, filtersKey === hydratedFiltersKeyRef.current ? requestedOffset : 0);
    }, [filters.statusGroup, fetchIssues, shouldShowPaywall, dispatch, appId, isUrlHydrated]);

    React.useEffect(() => {
        if (!isUrlHydrated) return;
        // Release-scoped triage ignores the Live/Next and Version filters
        const urlFilters = releaseId ? { ...filters, releaseFilter: 'none' as const, versions: [] } : filters;
        const nextSearch = buildTriageSearch(urlFilters, Math.floor(requestedOffset / PAGE_SIZE) + 1, location.search);
        if (nextSearch === location.search.replace(/^\?/, '')) return;
        history.replace({ ...location, search: nextSearch });
    }, [filters, requestedOffset, releaseId, location, history, isUrlHydrated]);

    const toggleReleaseFilter = (target: TriageReleaseFilter) => {
        // Re-clicking the active filter clears it; the two are mutually exclusive.
        dispatch(setTriageReleaseFilter({ appId, releaseFilter: filters.releaseFilter === target ? 'none' : target }));
    };

    const isLiveFilterDisabled = shouldShowPaywall || !liveVersion;
    const isNextFilterDisabled = shouldShowPaywall || !nextVersion;

    const releaseFilterButtonStyle = (active: boolean, disabled?: boolean): React.CSSProperties =>
        disabled ?
            { color: antGrey.grey8 }
        :   {
                color: active ? runwayColors.white : antGrey.grey8,
                backgroundColor: active ? rwColors.primary : runwayColors.white,
                borderColor: active ? rwColors.primary : antGrey.grey5,
            };

    // Server-side pages: the offset the store echoes back is the page currently on screen.
    const currentPage = Math.floor((offset ?? 0) / PAGE_SIZE) + 1;
    const onPageChange = (page: number) => {
        // Paginating means the user has moved on from the issue they just created or merged.
        dispatch(clearPinnedIssue({ appId }));
        fetchIssues(filters.statusGroup, (page - 1) * PAGE_SIZE);
    };

    const isFetchingIssues = useIsLoading(FetchablePaginatedEntityType.Issues, appId);

    const onTabChange = (key: string) => {
        dispatch(setTriageStatusGroup({ appId, statusGroup: key as TriageStatusGroup }));
    };

    const counts = useSelector((state: RootState) => selectIssuesCounts(state, appId));
    const { openCount, closedCount } =
        shouldShowPaywall ? { openCount: PAYWALL_ISSUES.length, closedCount: 0 } : counts;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '24px 32px 32px' }}>
            <Tabs
                className="triage-issues-tabs"
                activeKey={filters.statusGroup}
                onChange={onTabChange}
                items={[
                    {
                        key: 'open',
                        label: tabLabel('Open', openCount, filters.statusGroup === 'open'),
                    },
                    {
                        key: 'closed',
                        disabled: shouldShowPaywall,
                        label: tabLabel('Closed', closedCount, filters.statusGroup === 'closed'),
                    },
                ]}
                tabBarExtraContent={{
                    right: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isSelectMode ?
                                <Button
                                    icon={<CloseOutlined style={{ color: runwayColors.statusRed }} />}
                                    onClick={cancelSelect}
                                    style={filterButtonTextStyle}
                                >
                                    Cancel select
                                </Button>
                            :   <>
                                    {!releaseId && (
                                        <>
                                            <Button
                                                size="small"
                                                disabled={isLiveFilterDisabled}
                                                onClick={() => toggleReleaseFilter('live')}
                                                icon={
                                                    <RocketOutlined
                                                        style={{
                                                            color:
                                                                (
                                                                    filters.releaseFilter === 'live' &&
                                                                    !isLiveFilterDisabled
                                                                ) ?
                                                                    runwayColors.white
                                                                :   antGrey.grey6,
                                                        }}
                                                    />
                                                }
                                                style={releaseFilterButtonStyle(
                                                    filters.releaseFilter === 'live',
                                                    isLiveFilterDisabled,
                                                )}
                                            >
                                                Live release
                                                {filters.releaseFilter === 'live' && (
                                                    <CloseOutlined
                                                        style={{
                                                            color: runwayColors.white,
                                                            marginLeft: 10,
                                                        }}
                                                    />
                                                )}
                                            </Button>
                                            <Button
                                                size="small"
                                                disabled={isNextFilterDisabled}
                                                onClick={() => toggleReleaseFilter('next')}
                                                icon={
                                                    <ClockCircleOutlined
                                                        style={{
                                                            color:
                                                                (
                                                                    filters.releaseFilter === 'next' &&
                                                                    !isNextFilterDisabled
                                                                ) ?
                                                                    runwayColors.white
                                                                :   antGrey.grey6,
                                                        }}
                                                    />
                                                }
                                                style={releaseFilterButtonStyle(
                                                    filters.releaseFilter === 'next',
                                                    isNextFilterDisabled,
                                                )}
                                            >
                                                Next release
                                                {filters.releaseFilter === 'next' && (
                                                    <CloseOutlined
                                                        style={{
                                                            color: runwayColors.white,
                                                            marginLeft: 10,
                                                        }}
                                                    />
                                                )}
                                            </Button>
                                            <Divider
                                                type="vertical"
                                                style={{ height: 40, borderColor: antGrey.grey4 }}
                                            />
                                        </>
                                    )}
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAManageTriageIssues}
                                        icon={<CheckSquareOutlined style={filterButtonIconStyle} />}
                                        style={filterButtonTextStyle}
                                        disabled={shouldShowPaywall}
                                        onClick={() => setIsSelectMode(true)}
                                    >
                                        Select
                                    </PermissionedActionButton>
                                    <Dropdown
                                        trigger={['click']}
                                        disabled={shouldShowPaywall}
                                        menu={{
                                            items: SORT_OPTIONS.map((opt) => ({ key: opt.value, label: opt.label })),
                                            selectedKeys: [filters.sortBy],
                                            onClick: ({ key }) =>
                                                dispatch(setTriageSortBy({ appId, sortBy: key as TriageSortBy })),
                                        }}
                                    >
                                        <Button
                                            icon={<AimOutlined style={filterButtonIconStyle} />}
                                            style={filterButtonTextStyle}
                                            disabled={shouldShowPaywall}
                                        >
                                            Sort: {sortLabel(filters.sortBy)}
                                        </Button>
                                    </Dropdown>
                                    <Badge
                                        count={appliedFilterCount}
                                        size="small"
                                        style={{ backgroundColor: rwColors.primary }}
                                    >
                                        <Popover
                                            trigger="click"
                                            placement="bottomRight"
                                            destroyTooltipOnHide
                                            overlayInnerStyle={{ padding: 0, overflow: 'hidden' }}
                                            open={isFilterPopoverOpen}
                                            onOpenChange={setIsFilterPopoverOpen}
                                            content={
                                                <TriageFilters
                                                    orgId={orgId}
                                                    appId={appId}
                                                    showVersionFilter={isVersionFilterActive}
                                                    onApply={() => setIsFilterPopoverOpen(false)}
                                                />
                                            }
                                        >
                                            <Button
                                                icon={<FilterOutlined style={filterButtonIconStyle} />}
                                                style={filterButtonTextStyle}
                                                disabled={shouldShowPaywall}
                                            >
                                                Filter
                                            </Button>
                                        </Popover>
                                    </Badge>
                                </>
                            }
                        </div>
                    ),
                }}
            />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    marginTop: 8,
                }}
            >
                <div style={{ flex: '0 1 875px', minWidth: 0 }}>
                    <Input.Search
                        placeholder="Search"
                        enterButton={
                            <Button
                                disabled={shouldShowPaywall}
                                icon={
                                    <SearchOutlined
                                        style={{ color: shouldShowPaywall ? antGrey.grey5 : antGrey.grey7 }}
                                    />
                                }
                            />
                        }
                        allowClear
                        disabled={shouldShowPaywall}
                        value={searchInput}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onSearch={commitSearch}
                        style={{ width: '100%' }}
                    />
                </div>
                {isSelectMode ?
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        {filters.statusGroup === 'closed' ?
                            <PermissionedActionButton
                                actionType={RunwayUserAction.UAManageTriageIssues}
                                icon={
                                    <UndoOutlined
                                        style={{
                                            color: selectedIssueIds.size < 1 ? undefined : rwColors.statusYellow,
                                        }}
                                    />
                                }
                                disabled={selectedIssueIds.size < 1}
                                loading={isBulkReopening}
                                onClick={onBulkReopen}
                            >
                                Reopen
                            </PermissionedActionButton>
                        :   <>
                                {selectionHasFixRequest ?
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAManageTriageIssues}
                                        type="primary"
                                        icon={<FolderFilled />}
                                        disabled={selectedIssueIds.size < 2}
                                        onClick={() => openGroupModal(false)}
                                        style={{
                                            backgroundColor: rwColors.primary,
                                            borderColor: rwColors.primary,
                                            color: runwayColors.white,
                                            opacity: selectedIssueIds.size < 2 ? 0.5 : undefined,
                                        }}
                                    >
                                        Merge into existing issue with fix request
                                    </PermissionedActionButton>
                                :   <>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAManageTriageIssues}
                                            type="primary"
                                            icon={<FolderFilled />}
                                            disabled={selectedIssueIds.size < 2}
                                            onClick={() => openGroupModal(false)}
                                            style={{
                                                backgroundColor: rwColors.primary,
                                                borderColor: rwColors.primary,
                                                color: runwayColors.white,
                                                opacity: selectedIssueIds.size < 2 ? 0.5 : undefined,
                                            }}
                                        >
                                            Merge issues
                                        </PermissionedActionButton>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAManageTriageIssues}
                                            type="primary"
                                            icon={<ToolFilled />}
                                            disabled={selectedIssueIds.size < 2}
                                            onClick={() => openGroupModal(true)}
                                            style={{
                                                backgroundColor: rwColors.primary,
                                                borderColor: rwColors.primary,
                                                color: runwayColors.white,
                                                opacity: selectedIssueIds.size < 2 ? 0.5 : undefined,
                                            }}
                                        >
                                            Merge and escalate to fix request
                                        </PermissionedActionButton>
                                    </>
                                }
                                <PermissionedActionButton
                                    actionType={RunwayUserAction.UAManageTriageIssues}
                                    icon={<CloseCircleOutlined style={{ color: runwayColors.white }} />}
                                    disabled={selectedIssueIds.size < 1}
                                    loading={isBulkTransitioning}
                                    onClick={onBulkNotAPriority}
                                    style={{
                                        backgroundColor: antGrey.grey7,
                                        color: runwayColors.white,
                                        borderColor: antGrey.grey7,
                                        opacity: selectedIssueIds.size < 1 ? 0.5 : undefined,
                                    }}
                                >
                                    Not a priority
                                </PermissionedActionButton>
                            </>
                        }
                    </div>
                :   <Button
                        icon={<PlusOutlined style={{ color: shouldShowPaywall ? antGrey.grey6 : rwColors.primary }} />}
                        disabled={shouldShowPaywall}
                        onClick={() => setIsCreateModalOpen(true)}
                        style={{
                            marginLeft: 'auto',
                            color: shouldShowPaywall ? antGrey.grey6 : rwColors.primary,
                            backgroundColor: shouldShowPaywall ? antGrey.grey3 : colorWithAlpha(rwColors.primary, 0.1),
                            borderColor: 'transparent',
                            opacity: shouldShowPaywall ? 0.6 : undefined,
                            paddingLeft: 32,
                            paddingRight: 32,
                            visibility: filters.statusGroup === 'open' ? 'visible' : 'hidden',
                        }}
                    >
                        Add a new issue
                    </Button>
                }
            </div>
            {shouldShowPaywall ?
                <EmptyStateWrapper
                    featureSections={[FeatureSection.Triage]}
                    shouldForceEnable
                    shouldCenterContent
                    appId={appId}
                    releaseId={null}
                    paywallContent={<div className="org-overview-paywall-content">{paywallContent}</div>}
                >
                    <div style={{ paddingTop: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                                opacity: 0.5,
                                pointerEvents: 'none',
                            }}
                        >
                            {PAYWALL_ISSUES.map((issue) => (
                                <TriageIssueCard
                                    key={issue.id}
                                    issue={{ ...issue, appId }}
                                    orgId={orgId}
                                    viewType={viewType}
                                    disabled
                                />
                            ))}
                        </div>
                    </div>
                </EmptyStateWrapper>
            :   <>
                    {isFetchingIssues && (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 16 }}>
                            <Spin
                                indicator={<LoadingOutlined style={{ fontSize: 18, color: rwColors.primary }} spin />}
                            />
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 24 }}>
                        {issues.map((issue) => (
                            <TriageIssueCard
                                key={issue.id}
                                issue={issue}
                                orgId={orgId}
                                viewType={viewType}
                                basePath={basePath}
                                selectMode={isSelectMode}
                                selected={selectedIssueIds.has(issue.id)}
                                selectDisabled={
                                    selectionHasFixRequest && !!issue.fixRequest && !selectedIssueIds.has(issue.id)
                                }
                                onToggleSelected={() => toggleIssueSelected(issue)}
                            />
                        ))}
                        {!isFetchingIssues && issues.length === 0 && (
                            <Empty
                                className="triage-empty"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    filters.statusGroup === 'open' ? 'No open issues found.' : 'No closed issues found.'
                                }
                                style={{ paddingTop: 64 }}
                            />
                        )}
                        <Pagination
                            size="small"
                            hideOnSinglePage
                            showSizeChanger={false}
                            current={currentPage}
                            pageSize={PAGE_SIZE}
                            total={filters.statusGroup === 'open' ? openCount : closedCount}
                            onChange={onPageChange}
                            // Extra bottom clearance: the Runway support bubble floats over this corner.
                            style={{ alignSelf: 'flex-end', paddingTop: 8, paddingBottom: 60 }}
                        />
                    </div>
                </>
            }
            <CreateIssueModal
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                orgId={orgId}
                appId={appId}
                lockedReleaseId={releaseId}
            />
            <MergeIssuesModal
                open={isGroupModalOpen}
                onClose={() => setIsGroupModalOpen(false)}
                onGrouped={clearSelection}
                orgId={orgId}
                appId={appId}
                selectedIssues={selectedIssues}
                viewType={viewType}
                escalate={isEscalateMode}
            />
        </div>
    );
};
